import { motion } from 'framer-motion';
import React, { FC } from 'react';
import cx from 'classnames';
import { useId } from '@fluentui/react-hooks';

import theme from '../theme';

import Text from './Text';

export interface CheckboxProps {
  /**
   * A label to be displayed for the checkbox.
   * */
  label?: string;
  /**
   * A value to be displayed for the checkbox.
   * */
  value?: string;
  /**
   * Whether the checkbox is checked or not.
   * */
  checked?: boolean;

  /**
   * Whether the checkbox is disabled or not.
   * */
  disabled?: boolean;

  /**
   * Whether the checkbox is indeterminate or not.
   * */
  indeterminate?: boolean;

  /**
   * A callback function to be called when the checkbox is clicked.
   * */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const Checkbox: FC<CheckboxProps> = ({
  label,
  value,
  checked,
  disabled,
  indeterminate,
  onChange,
  ...props
}) => {
  const id = useId();

  return (
    <button
      className="flex items-center justify-start w-full gap-3 bg-transparent border-0 cursor-pointer"
      type="button"
      onClick={() => {
        if (onChange) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange({ target: { value, checked: !checked } } as any);
        }
      }}
      {...props}
    >
      <motion.svg
        className={cx(
          'fill-white w-4 h-4 border border-solid rounded-sm transition-colors',
          indeterminate && 'bg-clari-blue/500 border-clari-blue/500',
          checked &&
            'text-primary bg-clari-blue/500 hover:bg-clari-blue/600 active:bg-clari-blue/700 border-clari-blue/500',
          !checked && 'hover:bg-gray4 active:bg-gray4_5',
          !checked && !indeterminate && 'border-gray1',
        )}
        viewBox="0 0 20 20"
      >
        {indeterminate && (
          <path d="M4,11h12v2H4V11z M4,11h12v2H4V11z" fill="white" />
        )}
        {/* draw a checkmark when checked */}
        <motion.path
          initial={{ pathLength: 0 }}
          animate={{ pathLength: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          d="M 14.639 5.667 L 7.812 11.885 L 4.654 8.838 L 3.125 10.355 L 7.853 14.295 L 15.96 6.999 L 14.639 5.667 Z"
          fill={theme.gray4_5}
        />
      </motion.svg>
      {label && (
        <label className="cursor-pointer" htmlFor={id}>
          <Text weight="semibold" variant="body">
            {label}
          </Text>
        </label>
      )}
    </button>
  );
};

export default Checkbox;
